import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { resolveReviewStatus } from "@hooks/useJobResults";
import type { UseJobResultsState } from "@hooks/useJobResults.types";
import type { MappingResultRow } from "@models/mappingResult";
import { ReviewStatus } from "@models/job";

export interface UseBulkAcceptResult {
  bulkOpen: boolean;
  setBulkOpen: (open: boolean) => void;
  bulkThreshold: number;
  setBulkThreshold: (value: number) => void;
  bulkRunning: boolean;
  bulkSummary: string | null;
  setBulkSummary: (summary: string | null) => void;
  /** Pending rows whose top-match similarity is at or above the threshold. */
  bulkEligibleRows: MappingResultRow[];
  handleBulkAccept: () => Promise<void>;
}

/**
 * Bulk-accept controls: tracks the similarity threshold, derives the set of
 * eligible pending rows, and accepts them in one batched call.
 */
export const useBulkAccept = (
  results: UseJobResultsState,
): UseBulkAcceptResult => {
  const { t } = useTranslation();
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkThreshold, setBulkThreshold] = useState<number>(90);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkSummary, setBulkSummary] = useState<string | null>(null);

  const bulkEligibleRows = useMemo(() => {
    return results.rows.filter((row) => {
      const effective = resolveReviewStatus(
        row,
        results.edits.get(row.rowIndex),
      );
      if (effective !== ReviewStatus.Pending) return false;
      // No target, nothing to accept. A row whose score was never measured is
      // skipped too: "accept everything above N%" cannot include a row that
      // has no percentage, and treating an absent score as 0 or 100 would
      // either hide it forever or bulk-accept it blind.
      const score = row.target?.score;
      if (typeof score !== "number") return false;
      return score >= bulkThreshold;
    });
  }, [results.rows, results.edits, bulkThreshold]);

  const handleBulkAccept = useCallback(async (): Promise<void> => {
    if (bulkEligibleRows.length === 0) return;
    setBulkRunning(true);
    setBulkSummary(null);
    try {
      const result = await results.bulkApplyEdits(
        bulkEligibleRows.map((row) => ({
          rowIndex: row.rowIndex,
          edit: { reviewStatus: ReviewStatus.Accepted },
        })),
      );
      setBulkSummary(
        t("jobResultsPage.bulk.summary", {
          succeeded: result.succeeded,
          failed: result.failed,
          defaultValue: `Accepted ${result.succeeded}, failed ${result.failed}.`,
        }),
      );
    } finally {
      setBulkRunning(false);
    }
  }, [bulkEligibleRows, results, t]);

  return {
    bulkOpen,
    setBulkOpen,
    bulkThreshold,
    setBulkThreshold,
    bulkRunning,
    bulkSummary,
    setBulkSummary,
    bulkEligibleRows,
    handleBulkAccept,
  };
};
